/**
 * Interactive setup wizard for ludicrous-gateway
 * Provides a beautiful CLI experience for configuring providers and routing
 */

import * as p from "@clack/prompts";
// @ts-expect-error picocolors has no type declarations
import color from "picocolors";
import * as fs from "node:fs";
import * as path from "node:path";
import * as os from "node:os";
import { execFileSync } from "node:child_process";
import yaml from "js-yaml";
import type { GatewayConfig, ProviderConfig, RoutingRuleConfig } from "../types/config.js";

/**
 * Environment variable names for each provider type
 */
const PROVIDER_ENV_VARS: Record<string, string> = {
  anthropic: "ANTHROPIC_API_KEY",
  openai: "OPENAI_API_KEY",
  zai: "ZAI_API_KEY",
  gemini: "GOOGLE_API_KEY",
  grok: "XAI_API_KEY",
  mistral: "MISTRAL_API_KEY",
  deepseek: "DEEPSEEK_API_KEY",
};

/**
 * Track which environment variables were configured during setup
 */
const configuredEnvVars: Map<string, string> = new Map();
import {
  testAnthropicConnection,
  testZaiConnection,
  testOllamaConnection,
  testGrokConnection,
  testOpenAIConnection,
  testGeminiConnection,
  testMistralConnection,
  testDeepSeekConnection,
  testConnectionWithSpinner,
} from "./test-connection.js";
import { GROK_MODELS } from "../providers/grok.js";
import { OPENAI_MODELS } from "../providers/openai.js";
import { GEMINI_MODELS } from "../providers/gemini.js";
import { MISTRAL_MODELS } from "../providers/mistral.js";
import { DEEPSEEK_MODELS } from "../providers/deepseek.js";
import {
  generateIntelligentAssignments,
  getPreferenceDescription,
  getModelBenchmark,
  type OptimizationPreference,
} from "./benchmarks.js";

/**
 * Provider option for selection
 */
interface ProviderOption {
  value: string;
  label: string;
  hint?: string;
}

/**
 * Agent role definition with detection patterns and recommendations
 */
interface AgentRole {
  value: string;
  label: string;
  hint: string;
  patterns: string[];
  recommendedProvider: string;
  recommendedModel: string;
  priority: number;
}

/**
 * Role definitions with detection patterns and default provider recommendations
 */
const AGENT_ROLES: AgentRole[] = [
  {
    value: "planner",
    label: "Planner / Architect",
    hint: "High-level design, strategy, breaking down tasks",
    patterns: ["plan", "architect", "design system", "strategy", "decompose", "break down", "coordinate"],
    recommendedProvider: "anthropic",
    recommendedModel: "claude-opus-4-5-20251101",
    priority: 95,
  },
  {
    value: "coder",
    label: "Coder / Implementer",
    hint: "Writing code, implementing features",
    patterns: ["implement", "write code", "build", "create function", "code this", "add feature"],
    recommendedProvider: "zai",
    recommendedModel: "glm-4.7",
    priority: 60,
  },
  {
    value: "reviewer",
    label: "Code Reviewer",
    hint: "Reviewing code, finding issues",
    patterns: ["review", "check this code", "find issues", "critique", "analyze code", "code review"],
    recommendedProvider: "anthropic",
    recommendedModel: "claude-sonnet-4-20251114",
    priority: 75,
  },
  {
    value: "tester",
    label: "Test Writer",
    hint: "Writing tests, test coverage",
    patterns: ["write test", "unit test", "integration test", "test coverage", "add tests", "testing"],
    recommendedProvider: "zai",
    recommendedModel: "glm-4.7",
    priority: 65,
  },
  {
    value: "security",
    label: "Security Analyst",
    hint: "Security review, vulnerability analysis",
    patterns: ["security", "vulnerability", "CVE", "injection", "XSS", "CSRF", "authentication", "authorization", "OWASP"],
    recommendedProvider: "anthropic",
    recommendedModel: "claude-opus-4-5-20251101",
    priority: 90,
  },
  {
    value: "documentor",
    label: "Documentation Writer",
    hint: "Writing docs, READMEs, comments",
    patterns: ["document", "write docs", "README", "documentation", "explain this", "add comments", "JSDoc"],
    recommendedProvider: "zai",
    recommendedModel: "glm-4.5-air",
    priority: 50,
  },
  {
    value: "debugger",
    label: "Debugger / Troubleshooter",
    hint: "Debugging issues, fixing errors",
    patterns: ["debug", "troubleshoot", "error", "not working", "fix bug", "why is this", "broken"],
    recommendedProvider: "anthropic",
    recommendedModel: "claude-sonnet-4-20251114",
    priority: 80,
  },
  {
    value: "refactorer",
    label: "Refactoring Specialist",
    hint: "Code cleanup, restructuring",
    patterns: ["refactor", "clean up", "improve structure", "simplify", "restructure", "optimize code"],
    recommendedProvider: "zai",
    recommendedModel: "glm-4.7",
    priority: 55,
  },
  {
    value: "adversarial",
    label: "Adversarial / Red Team",
    hint: "Challenge assumptions, find flaws, devil's advocate",
    patterns: ["challenge", "find flaws", "devil's advocate", "what could go wrong", "critique my", "red team"],
    recommendedProvider: "deepseek",
    recommendedModel: "deepseek-reasoner",
    priority: 85,
  },
  {
    value: "researcher",
    label: "Researcher / Explorer",
    hint: "Exploring options, investigating solutions",
    patterns: ["research", "explore", "investigate", "compare options", "what are the", "pros and cons"],
    recommendedProvider: "zai",
    recommendedModel: "glm-4.7",
    priority: 70,
  },
];

/**
 * Available providers for configuration
 */
const AVAILABLE_PROVIDERS: ProviderOption[] = [
  {
    value: "anthropic",
    label: "Anthropic",
    hint: "Claude Max subscription or API key",
  },
  {
    value: "zai",
    label: "Z.AI",
    hint: "GLM models - cost effective",
  },
  {
    value: "ollama",
    label: "Ollama",
    hint: "Local models - privacy focused",
  },
  {
    value: "openai",
    label: "OpenAI",
    hint: "GPT-4o, o1 reasoning models",
  },
  {
    value: "gemini",
    label: "Google Gemini",
    hint: "Gemini 2.0, 1.5 Pro/Flash",
  },
  {
    value: "grok",
    label: "Grok (xAI)",
    hint: "Grok 2 models",
  },
  {
    value: "mistral",
    label: "Mistral AI",
    hint: "Mistral Large, Codestral",
  },
  {
    value: "deepseek",
    label: "DeepSeek",
    hint: "DeepSeek R1, V3, Coder",
  },
  {
    value: "kimi",
    label: "Kimi K2",
    hint: "Coming soon",
  },
];

/**
 * Display the wizard intro banner
 */
function displayBanner(): void {
  console.log();
  console.log(color.cyan("╭─────────────────────────────────────────────────╮"));
  console.log(color.cyan("│                                                 │"));
  console.log(color.cyan("│   ") + color.bold(color.yellow("⚡")) + color.bold(" Ludicrous Gateway Setup") + color.cyan("                    │"));
  console.log(color.cyan("│                                                 │"));
  console.log(color.cyan("│   ") + color.dim("Multi-agent LLM routing for Claude Code") + color.cyan("       │"));
  console.log(color.cyan("│                                                 │"));
  console.log(color.cyan("╰─────────────────────────────────────────────────╯"));
  console.log();
}

/**
 * Validate API key format
 */
function validateApiKey(value: string, _provider: string): string | undefined {
  if (!value || value.trim().length === 0) {
    return "API key is required";
  }

  // Basic length check - most API keys are at least 20 chars
  if (value.length < 10) {
    return "API key seems too short";
  }

  return undefined;
}

/**
 * Configure Anthropic provider
 */
async function configureAnthropic(): Promise<ProviderConfig | null> {
  p.log.step(color.bold("Anthropic Configuration"));

  const hasSubscription = await p.confirm({
    message: "Do you have a Claude Max subscription?",
    initialValue: true,
  });

  if (p.isCancel(hasSubscription)) return null;

  if (hasSubscription) {
    // Subscription passthrough can't be tested standalone - it only works
    // when requests come through Claude Code with auth headers
    p.note(
      "Subscription passthrough will forward your Claude Code auth headers.\n" +
        "This can't be tested here, but will work when you run Claude Code\n" +
        "with " + color.cyan("ANTHROPIC_BASE_URL") + " pointing to the gateway.",
      "Claude Max Mode"
    );

    return {
      name: "anthropic",
      type: "anthropic",
      enabled: true,
      subscription_passthrough: true,
    };
  }

  // Configure with API key - loop to allow retrying
  while (true) {
    const apiKey = await p.password({
      message: "Enter your Anthropic API key:",
      validate: (v) => validateApiKey(v, "anthropic"),
    });

    if (p.isCancel(apiKey)) return null;

    // Test with API key
    const result = await testConnectionWithSpinner("Anthropic", () =>
      testAnthropicConnection("https://api.anthropic.com", apiKey)
    );

    if (result.success) {
      // Store env var reference instead of actual key
      const envVarName = PROVIDER_ENV_VARS.anthropic!;
      configuredEnvVars.set(envVarName, apiKey);

      p.log.info(`Your API key will be stored as ${color.cyan("${" + envVarName + "}")} in the config.`);
      p.log.info(`Set the environment variable: ${color.cyan(`export ${envVarName}="your-key"`)}`);

      return {
        name: "anthropic",
        type: "anthropic",
        enabled: true,
        subscription_passthrough: false,
        keys: [{ key: "${" + envVarName + "}" }],
      };
    }

    // Test failed - ask what to do
    const action = await p.select({
      message: "Connection test failed. What would you like to do?",
      options: [
        { value: "retry", label: "Re-enter API key", hint: "Try a different key" },
        { value: "skip", label: "Skip this provider" },
        { value: "add", label: "Add anyway", hint: "Use this key despite the error" },
      ],
    });

    if (p.isCancel(action) || action === "skip") return null;

    if (action === "add") {
      // Store env var reference and return config anyway
      const envVarName = PROVIDER_ENV_VARS.anthropic!;
      configuredEnvVars.set(envVarName, apiKey);

      p.log.info(`Your API key will be stored as ${color.cyan("${" + envVarName + "}")} in the config.`);
      p.log.info(`Set the environment variable: ${color.cyan(`export ${envVarName}="your-key"`)}`);

      return {
        name: "anthropic",
        type: "anthropic",
        enabled: true,
        subscription_passthrough: false,
        keys: [{ key: "${" + envVarName + "}" }],
      };
    }
    // action === "retry" - loop continues
  }
}

/**
 * Configure Z.AI provider
 */
async function configureZai(): Promise<ProviderConfig | null> {
  p.log.step(color.bold("Z.AI Configuration"));

  p.note(
    "Get your API key from the Z.AI dashboard:\n" +
      color.cyan("https://z.ai/dashboard") + "\n\n" +
      color.dim("Your API key may start with various prefixes depending on your account."),
    "Z.AI Setup"
  );

  // Loop to allow retrying API key entry
  while (true) {
    const apiKey = await p.password({
      message: "Enter your Z.AI API key:",
      validate: (v) => validateApiKey(v, "zai"),
    });

    if (p.isCancel(apiKey)) return null;

    // Test connection
    const result = await testConnectionWithSpinner("Z.AI", () =>
      testZaiConnection(apiKey)
    );

    if (result.success) {
      // Select default model
      const defaultModel = await p.select({
        message: "Select default GLM model:",
        options: [
          { value: "GLM-4.7", label: "GLM-4.7", hint: "Best quality" },
          { value: "GLM-4.5-Air", label: "GLM-4.5-Air", hint: "Faster, cheaper" },
        ],
      });

      if (p.isCancel(defaultModel)) return null;

      // Store env var reference instead of actual key
      const envVarName = PROVIDER_ENV_VARS.zai!;
      configuredEnvVars.set(envVarName, apiKey);

      p.log.info(`Your API key will be stored as ${color.cyan("${" + envVarName + "}")} in the config.`);
      p.log.info(`Set the environment variable: ${color.cyan(`export ${envVarName}="your-key"`)}`);

      return {
        name: "zai",
        type: "zai",
        enabled: true,
        keys: [{ key: "${" + envVarName + "}" }],
        default_model: defaultModel as string,
        model_aliases: {
          "claude-opus-4-5-20251101": defaultModel as string,
          "claude-sonnet-4-20250514": "GLM-4.5-Air",
        },
      };
    }

    // Test failed - ask what to do
    const action = await p.select({
      message: "Connection test failed. What would you like to do?",
      options: [
        { value: "retry", label: "Re-enter API key", hint: "Try a different key" },
        { value: "skip", label: "Skip this provider" },
        { value: "add", label: "Add anyway", hint: "Use this key despite the error" },
      ],
    });

    if (p.isCancel(action) || action === "skip") return null;

    if (action === "add") {
      // Select default model
      const defaultModel = await p.select({
        message: "Select default GLM model:",
        options: [
          { value: "GLM-4.7", label: "GLM-4.7", hint: "Best quality" },
          { value: "GLM-4.5-Air", label: "GLM-4.5-Air", hint: "Faster, cheaper" },
        ],
      });

      if (p.isCancel(defaultModel)) return null;

      // Store env var reference instead of actual key
      const envVarName = PROVIDER_ENV_VARS.zai!;
      configuredEnvVars.set(envVarName, apiKey);

      p.log.info(`Your API key will be stored as ${color.cyan("${" + envVarName + "}")} in the config.`);
      p.log.info(`Set the environment variable: ${color.cyan(`export ${envVarName}="your-key"`)}`);

      return {
        name: "zai",
        type: "zai",
        enabled: true,
        keys: [{ key: "${" + envVarName + "}" }],
        default_model: defaultModel as string,
        model_aliases: {
          "claude-opus-4-5-20251101": defaultModel as string,
          "claude-sonnet-4-20250514": "GLM-4.5-Air",
        },
      };
    }
    // action === "retry" - loop continues
  }
}

/**
 * Configure Ollama provider
 */
async function configureOllama(): Promise<ProviderConfig | null> {
  p.log.step(color.bold("Ollama Configuration"));

  // Loop to allow retrying URL entry
  while (true) {
    const baseUrl = await p.text({
      message: "Enter Ollama base URL:",
      placeholder: "http://localhost:11434",
      initialValue: "http://localhost:11434",
      validate: (v) => {
        try {
          new URL(v);
          return undefined;
        } catch {
          return "Invalid URL format";
        }
      },
    });

    if (p.isCancel(baseUrl)) return null;

    // Test connection and get models
    const result = await testConnectionWithSpinner("Ollama", () =>
      testOllamaConnection(baseUrl)
    );

    if (result.success) {
      // Select models to use
      let selectedModel = "llama3.2";

      if (result.models && result.models.length > 0) {
        const modelChoice = await p.select({
          message: "Select default model:",
          options: result.models.map((m) => ({
            value: m,
            label: m,
          })),
        });

        if (p.isCancel(modelChoice)) return null;
        selectedModel = modelChoice as string;

        p.log.success(`Found ${result.models.length} installed model(s)`);
      } else {
        p.log.warn("No models found. Pull a model with: ollama pull llama3.2");
      }

      return {
        name: "ollama",
        type: "ollama",
        enabled: true,
        base_url: baseUrl,
        default_model: selectedModel,
        model_aliases: {
          "claude-opus-4-5-20251101": selectedModel,
          "claude-sonnet-4-20250514": selectedModel,
        },
      };
    }

    // Test failed - show help and ask what to do
    p.note(
      "Make sure Ollama is running:\n" +
        color.cyan("  ollama serve") +
        "\n\nOr install from:\n" +
        color.cyan("  https://ollama.ai"),
      "Ollama Not Running"
    );

    const action = await p.select({
      message: "Connection test failed. What would you like to do?",
      options: [
        { value: "retry", label: "Re-enter URL", hint: "Try a different URL" },
        { value: "skip", label: "Skip this provider" },
        { value: "add", label: "Add anyway", hint: "Configure later when Ollama is running" },
      ],
    });

    if (p.isCancel(action) || action === "skip") return null;

    if (action === "add") {
      return {
        name: "ollama",
        type: "ollama",
        enabled: true,
        base_url: baseUrl,
        default_model: "llama3.2",
      };
    }
    // action === "retry" - loop continues
  }
}

/**
 * Configure Grok (xAI) provider
 */
async function configureGrok(): Promise<ProviderConfig | null> {
  p.log.step(color.bold("Grok (xAI) Configuration"));

  p.note(
    "Get your API key from the xAI dashboard:\n" +
      color.cyan("https://console.x.ai"),
    "Grok Setup"
  );

  // Loop to allow retrying API key entry
  while (true) {
    const apiKey = await p.password({
      message: "Enter your xAI API key:",
      validate: (v) => validateApiKey(v, "grok"),
    });

    if (p.isCancel(apiKey)) return null;

    // Test connection
    const result = await testConnectionWithSpinner("Grok", () =>
      testGrokConnection(apiKey)
    );

    if (result.success) {
      // Select default model
      const defaultModel = await p.select({
        message: "Select default Grok model:",
        options: GROK_MODELS.map((m) => ({
          value: m.value,
          label: m.label,
        })),
      });

      if (p.isCancel(defaultModel)) return null;

      // Store env var reference instead of actual key
      const envVarName = PROVIDER_ENV_VARS.grok!;
      configuredEnvVars.set(envVarName, apiKey);

      p.log.info(`Your API key will be stored as ${color.cyan("${" + envVarName + "}")} in the config.`);
      p.log.info(`Set the environment variable: ${color.cyan(`export ${envVarName}="your-key"`)}`);

      return {
        name: "grok",
        type: "grok",
        enabled: true,
        keys: [{ key: "${" + envVarName + "}" }],
        default_model: defaultModel as string,
        model_aliases: {
          "claude-opus-4-5-20251101": "grok-2",
          "claude-sonnet-4-20251114": "grok-2",
          "claude-3-5-haiku-20241022": "grok-2-mini",
        },
      };
    }

    // Test failed - ask what to do
    const action = await p.select({
      message: "Connection test failed. What would you like to do?",
      options: [
        { value: "retry", label: "Re-enter API key", hint: "Try a different key" },
        { value: "skip", label: "Skip this provider" },
        { value: "add", label: "Add anyway", hint: "Use this key despite the error" },
      ],
    });

    if (p.isCancel(action) || action === "skip") return null;

    if (action === "add") {
      // Select default model
      const defaultModel = await p.select({
        message: "Select default Grok model:",
        options: GROK_MODELS.map((m) => ({
          value: m.value,
          label: m.label,
        })),
      });

      if (p.isCancel(defaultModel)) return null;

      // Store env var reference instead of actual key
      const envVarName = PROVIDER_ENV_VARS.grok!;
      configuredEnvVars.set(envVarName, apiKey);

      p.log.info(`Your API key will be stored as ${color.cyan("${" + envVarName + "}")} in the config.`);
      p.log.info(`Set the environment variable: ${color.cyan(`export ${envVarName}="your-key"`)}`);

      return {
        name: "grok",
        type: "grok",
        enabled: true,
        keys: [{ key: "${" + envVarName + "}" }],
        default_model: defaultModel as string,
        model_aliases: {
          "claude-opus-4-5-20251101": "grok-2",
          "claude-sonnet-4-20251114": "grok-2",
          "claude-3-5-haiku-20241022": "grok-2-mini",
        },
      };
    }
    // action === "retry" - loop continues
  }
}

/**
 * Configure OpenAI provider
 */
async function configureOpenAI(): Promise<ProviderConfig | null> {
  p.log.step(color.bold("OpenAI Configuration"));

  p.note(
    "Get your API key from the OpenAI dashboard:\n" +
      color.cyan("https://platform.openai.com/api-keys"),
    "OpenAI Setup"
  );

  // Loop to allow retrying API key entry
  while (true) {
    const apiKey = await p.password({
      message: "Enter your OpenAI API key:",
      validate: (v) => validateApiKey(v, "openai"),
    });

    if (p.isCancel(apiKey)) return null;

    // Test connection
    const result = await testConnectionWithSpinner("OpenAI", () =>
      testOpenAIConnection(apiKey)
    );

    if (result.success) {
      // Select default model
      const defaultModel = await p.select({
        message: "Select default OpenAI model:",
        options: OPENAI_MODELS.map((m) => ({
          value: m.value,
          label: m.label,
        })),
      });

      if (p.isCancel(defaultModel)) return null;

      // Store env var reference instead of actual key
      const envVarName = PROVIDER_ENV_VARS.openai!;
      configuredEnvVars.set(envVarName, apiKey);

      p.log.info(`Your API key will be stored as ${color.cyan("${" + envVarName + "}")} in the config.`);
      p.log.info(`Set the environment variable: ${color.cyan(`export ${envVarName}="your-key"`)}`);

      return {
        name: "openai",
        type: "openai",
        enabled: true,
        keys: [{ key: "${" + envVarName + "}" }],
        default_model: defaultModel as string,
        model_aliases: {
          "claude-opus-4-5-20251101": "gpt-4o",
          "claude-sonnet-4-20251114": "gpt-4o-mini",
          "claude-3-5-haiku-20241022": "gpt-4o-mini",
        },
      };
    }

    // Test failed - ask what to do
    const action = await p.select({
      message: "Connection test failed. What would you like to do?",
      options: [
        { value: "retry", label: "Re-enter API key", hint: "Try a different key" },
        { value: "skip", label: "Skip this provider" },
        { value: "add", label: "Add anyway", hint: "Use this key despite the error" },
      ],
    });

    if (p.isCancel(action) || action === "skip") return null;

    if (action === "add") {
      // Select default model
      const defaultModel = await p.select({
        message: "Select default OpenAI model:",
        options: OPENAI_MODELS.map((m) => ({
          value: m.value,
          label: m.label,
        })),
      });

      if (p.isCancel(defaultModel)) return null;

      // Store env var reference instead of actual key
      const envVarName = PROVIDER_ENV_VARS.openai!;
      configuredEnvVars.set(envVarName, apiKey);

      p.log.info(`Your API key will be stored as ${color.cyan("${" + envVarName + "}")} in the config.`);
      p.log.info(`Set the environment variable: ${color.cyan(`export ${envVarName}="your-key"`)}`);

      return {
        name: "openai",
        type: "openai",
        enabled: true,
        keys: [{ key: "${" + envVarName + "}" }],
        default_model: defaultModel as string,
        model_aliases: {
          "claude-opus-4-5-20251101": "gpt-4o",
          "claude-sonnet-4-20251114": "gpt-4o-mini",
          "claude-3-5-haiku-20241022": "gpt-4o-mini",
        },
      };
    }
    // action === "retry" - loop continues
  }
}

/**
 * Configure Google Gemini provider
 */
async function configureGemini(): Promise<ProviderConfig | null> {
  p.log.step(color.bold("Google Gemini Configuration"));

  p.note(
    "Get your API key from Google AI Studio:\n" +
      color.cyan("https://aistudio.google.com/apikey"),
    "Gemini Setup"
  );

  // Loop to allow retrying API key entry
  while (true) {
    const apiKey = await p.password({
      message: "Enter your Google Gemini API key:",
      validate: (v) => validateApiKey(v, "gemini"),
    });

    if (p.isCancel(apiKey)) return null;

    // Test connection
    const result = await testConnectionWithSpinner("Gemini", () =>
      testGeminiConnection(apiKey)
    );

    if (result.success) {
      // Select default model
      const defaultModel = await p.select({
        message: "Select default Gemini model:",
        options: GEMINI_MODELS.map((m) => ({
          value: m.value,
          label: m.label,
        })),
      });

      if (p.isCancel(defaultModel)) return null;

      // Store env var reference instead of actual key
      const envVarName = PROVIDER_ENV_VARS.gemini!;
      configuredEnvVars.set(envVarName, apiKey);

      p.log.info(`Your API key will be stored as ${color.cyan("${" + envVarName + "}")} in the config.`);
      p.log.info(`Set the environment variable: ${color.cyan(`export ${envVarName}="your-key"`)}`);

      return {
        name: "gemini",
        type: "gemini",
        enabled: true,
        keys: [{ key: "${" + envVarName + "}" }],
        default_model: defaultModel as string,
        model_aliases: {
          "claude-opus-4-5-20251101": "gemini-2.0-flash-exp",
          "claude-sonnet-4-20251114": "gemini-1.5-flash",
          "claude-3-5-haiku-20241022": "gemini-1.5-flash-8b",
        },
      };
    }

    // Test failed - ask what to do
    const action = await p.select({
      message: "Connection test failed. What would you like to do?",
      options: [
        { value: "retry", label: "Re-enter API key", hint: "Try a different key" },
        { value: "skip", label: "Skip this provider" },
        { value: "add", label: "Add anyway", hint: "Use this key despite the error" },
      ],
    });

    if (p.isCancel(action) || action === "skip") return null;

    if (action === "add") {
      // Select default model
      const defaultModel = await p.select({
        message: "Select default Gemini model:",
        options: GEMINI_MODELS.map((m) => ({
          value: m.value,
          label: m.label,
        })),
      });

      if (p.isCancel(defaultModel)) return null;

      // Store env var reference instead of actual key
      const envVarName = PROVIDER_ENV_VARS.gemini!;
      configuredEnvVars.set(envVarName, apiKey);

      p.log.info(`Your API key will be stored as ${color.cyan("${" + envVarName + "}")} in the config.`);
      p.log.info(`Set the environment variable: ${color.cyan(`export ${envVarName}="your-key"`)}`);

      return {
        name: "gemini",
        type: "gemini",
        enabled: true,
        keys: [{ key: "${" + envVarName + "}" }],
        default_model: defaultModel as string,
        model_aliases: {
          "claude-opus-4-5-20251101": "gemini-2.0-flash-exp",
          "claude-sonnet-4-20251114": "gemini-1.5-flash",
          "claude-3-5-haiku-20241022": "gemini-1.5-flash-8b",
        },
      };
    }
    // action === "retry" - loop continues
  }
}

/**
 * Configure Mistral AI provider
 */
async function configureMistral(): Promise<ProviderConfig | null> {
  p.log.step(color.bold("Mistral AI Configuration"));

  p.note(
    "Get your API key from the Mistral console:\n" +
      color.cyan("https://console.mistral.ai/api-keys"),
    "Mistral Setup"
  );

  // Loop to allow retrying API key entry
  while (true) {
    const apiKey = await p.password({
      message: "Enter your Mistral API key:",
      validate: (v) => validateApiKey(v, "mistral"),
    });

    if (p.isCancel(apiKey)) return null;

    // Test connection
    const result = await testConnectionWithSpinner("Mistral", () =>
      testMistralConnection(apiKey)
    );

    if (result.success) {
      // Select default model
      const defaultModel = await p.select({
        message: "Select default Mistral model:",
        options: MISTRAL_MODELS.map((m) => ({
          value: m.value,
          label: m.label,
        })),
      });

      if (p.isCancel(defaultModel)) return null;

      // Store env var reference instead of actual key
      const envVarName = PROVIDER_ENV_VARS.mistral!;
      configuredEnvVars.set(envVarName, apiKey);

      p.log.info(`Your API key will be stored as ${color.cyan("${" + envVarName + "}")} in the config.`);
      p.log.info(`Set the environment variable: ${color.cyan(`export ${envVarName}="your-key"`)}`);

      return {
        name: "mistral",
        type: "mistral",
        enabled: true,
        keys: [{ key: "${" + envVarName + "}" }],
        default_model: defaultModel as string,
        model_aliases: {
          "claude-opus-4-5-20251101": "mistral-large-latest",
          "claude-sonnet-4-20251114": "mistral-medium-latest",
          "claude-3-5-haiku-20241022": "mistral-small-latest",
        },
      };
    }

    // Test failed - ask what to do
    const action = await p.select({
      message: "Connection test failed. What would you like to do?",
      options: [
        { value: "retry", label: "Re-enter API key", hint: "Try a different key" },
        { value: "skip", label: "Skip this provider" },
        { value: "add", label: "Add anyway", hint: "Use this key despite the error" },
      ],
    });

    if (p.isCancel(action) || action === "skip") return null;

    if (action === "add") {
      // Select default model
      const defaultModel = await p.select({
        message: "Select default Mistral model:",
        options: MISTRAL_MODELS.map((m) => ({
          value: m.value,
          label: m.label,
        })),
      });

      if (p.isCancel(defaultModel)) return null;

      // Store env var reference instead of actual key
      const envVarName = PROVIDER_ENV_VARS.mistral!;
      configuredEnvVars.set(envVarName, apiKey);

      p.log.info(`Your API key will be stored as ${color.cyan("${" + envVarName + "}")} in the config.`);
      p.log.info(`Set the environment variable: ${color.cyan(`export ${envVarName}="your-key"`)}`);

      return {
        name: "mistral",
        type: "mistral",
        enabled: true,
        keys: [{ key: "${" + envVarName + "}" }],
        default_model: defaultModel as string,
        model_aliases: {
          "claude-opus-4-5-20251101": "mistral-large-latest",
          "claude-sonnet-4-20251114": "mistral-medium-latest",
          "claude-3-5-haiku-20241022": "mistral-small-latest",
        },
      };
    }
    // action === "retry" - loop continues
  }
}

/**
 * Configure DeepSeek provider
 */
async function configureDeepSeek(): Promise<ProviderConfig | null> {
  p.log.step(color.bold("DeepSeek Configuration"));

  p.note(
    "Get your API key from the DeepSeek platform:\n" +
      color.cyan("https://platform.deepseek.com/api_keys"),
    "DeepSeek Setup"
  );

  // Loop to allow retrying API key entry
  while (true) {
    const apiKey = await p.password({
      message: "Enter your DeepSeek API key:",
      validate: (v) => validateApiKey(v, "deepseek"),
    });

    if (p.isCancel(apiKey)) return null;

    // Test connection
    const result = await testConnectionWithSpinner("DeepSeek", () =>
      testDeepSeekConnection(apiKey)
    );

    if (result.success) {
      // Select default model
      const defaultModel = await p.select({
        message: "Select default DeepSeek model:",
        options: DEEPSEEK_MODELS.map((m) => ({
          value: m.value,
          label: m.label,
        })),
      });

      if (p.isCancel(defaultModel)) return null;

      // Store env var reference instead of actual key
      const envVarName = PROVIDER_ENV_VARS.deepseek!;
      configuredEnvVars.set(envVarName, apiKey);

      p.log.info(`Your API key will be stored as ${color.cyan("${" + envVarName + "}")} in the config.`);
      p.log.info(`Set the environment variable: ${color.cyan(`export ${envVarName}="your-key"`)}`);

      return {
        name: "deepseek",
        type: "deepseek",
        enabled: true,
        keys: [{ key: "${" + envVarName + "}" }],
        default_model: defaultModel as string,
        model_aliases: {
          "claude-opus-4-5-20251101": "deepseek-reasoner",
          "claude-sonnet-4-20251114": "deepseek-chat",
          "claude-3-5-haiku-20241022": "deepseek-chat",
        },
      };
    }

    // Test failed - ask what to do
    const action = await p.select({
      message: "Connection test failed. What would you like to do?",
      options: [
        { value: "retry", label: "Re-enter API key", hint: "Try a different key" },
        { value: "skip", label: "Skip this provider" },
        { value: "add", label: "Add anyway", hint: "Use this key despite the error" },
      ],
    });

    if (p.isCancel(action) || action === "skip") return null;

    if (action === "add") {
      // Select default model
      const defaultModel = await p.select({
        message: "Select default DeepSeek model:",
        options: DEEPSEEK_MODELS.map((m) => ({
          value: m.value,
          label: m.label,
        })),
      });

      if (p.isCancel(defaultModel)) return null;

      // Store env var reference instead of actual key
      const envVarName = PROVIDER_ENV_VARS.deepseek!;
      configuredEnvVars.set(envVarName, apiKey);

      p.log.info(`Your API key will be stored as ${color.cyan("${" + envVarName + "}")} in the config.`);
      p.log.info(`Set the environment variable: ${color.cyan(`export ${envVarName}="your-key"`)}`);

      return {
        name: "deepseek",
        type: "deepseek",
        enabled: true,
        keys: [{ key: "${" + envVarName + "}" }],
        default_model: defaultModel as string,
        model_aliases: {
          "claude-opus-4-5-20251101": "deepseek-reasoner",
          "claude-sonnet-4-20251114": "deepseek-chat",
          "claude-3-5-haiku-20241022": "deepseek-chat",
        },
      };
    }
    // action === "retry" - loop continues
  }
}

/**
 * Get available models for a provider
 */
function getProviderModels(provider: ProviderConfig): { value: string; label: string; hint?: string }[] {
  switch (provider.type) {
    case "anthropic":
      return [
        { value: "claude-opus-4-5-20251101", label: "Claude Opus 4.5", hint: "Most capable" },
        { value: "claude-sonnet-4-20251114", label: "Claude Sonnet 4", hint: "Fast and capable" },
      ];
    case "zai":
      return [
        { value: "glm-4.7", label: "GLM-4.7", hint: "Best quality" },
        { value: "glm-4.5-air", label: "GLM-4.5-Air", hint: "Faster, cheaper" },
      ];
    case "ollama":
      return [
        { value: provider.default_model ?? "llama3.2", label: provider.default_model ?? "llama3.2", hint: "Local model" },
        { value: "qwen3:32b", label: "qwen3:32b", hint: "Alternative" },
      ];
    case "openai":
      return OPENAI_MODELS.map((m) => ({
        value: m.value,
        label: m.label,
      }));
    case "gemini":
      return GEMINI_MODELS.map((m) => ({
        value: m.value,
        label: m.label,
      }));
    case "grok":
      return GROK_MODELS.map((m) => ({
        value: m.value,
        label: m.label,
      }));
    case "mistral":
      return MISTRAL_MODELS.map((m) => ({
        value: m.value,
        label: m.label,
      }));
    case "deepseek":
      return DEEPSEEK_MODELS.map((m) => ({
        value: m.value,
        label: m.label,
      }));
    default:
      return [{ value: "default", label: "Default model" }];
  }
}

/**
 * Convert role configuration to a routing rule
 */
function roleToRule(role: AgentRole, provider: string, model?: string): RoutingRuleConfig {
  return {
    name: `${role.value}-routing`,
    conditions: {
      patterns: role.patterns,
    },
    route_to: {
      provider,
      model: model ?? role.recommendedModel,
    },
    priority: role.priority,
    enabled: true,
  };
}

/**
 * Display role-based routing summary table
 */
function displayRoleSummary(
  roleConfigs: Array<{ role: AgentRole; provider: string; model: string }>,
  optimizationPreference?: OptimizationPreference
): void {
  if (roleConfigs.length === 0) return;

  console.log();
  console.log(color.bold("  Role-Based Routing Summary"));
  if (optimizationPreference) {
    console.log(color.dim(`  Optimization: ${optimizationPreference}`));
  }
  console.log();

  // Calculate column widths - include cost column if we have optimization preference
  const roleWidth = Math.max(20, ...roleConfigs.map(rc => rc.role.label.length));
  const providerWidth = Math.max(12, ...roleConfigs.map(rc => rc.provider.length));
  const modelWidth = Math.max(18, ...roleConfigs.map(rc => rc.model.length));

  // Header
  let headerLine: string;
  if (optimizationPreference) {
    headerLine = `  ${"Role".padEnd(roleWidth)} ${"Provider".padEnd(providerWidth)} ${"Model".padEnd(modelWidth)} ${"$/MTok".padEnd(8)} ${"Quality".padEnd(7)}`;
  } else {
    headerLine = `  ${"Role".padEnd(roleWidth)} ${"Provider".padEnd(providerWidth)} ${"Model".padEnd(modelWidth)}`;
  }
  console.log(color.dim(headerLine));
  console.log(color.dim("  " + "-".repeat(headerLine.length - 2)));

  // Rows
  for (const { role, provider, model } of roleConfigs) {
    const roleCol = color.cyan(role.label.padEnd(roleWidth));
    const providerCol = provider.padEnd(providerWidth);
    const modelCol = color.dim(model.padEnd(modelWidth));

    if (optimizationPreference) {
      const benchmark = getModelBenchmark(provider, model);
      if (benchmark) {
        const priceCol = benchmark.pricePerMToken === 0
          ? color.green("free".padEnd(8))
          : color.yellow(`$${benchmark.pricePerMToken.toFixed(2)}`.padEnd(8));
        const qualityCol = color.magenta(`${benchmark.qualityScore}`.padEnd(7));
        console.log(`  ${roleCol} ${providerCol} ${modelCol} ${priceCol} ${qualityCol}`);
      } else {
        console.log(`  ${roleCol} ${providerCol} ${modelCol} ${color.dim("n/a".padEnd(8))} ${color.dim("n/a".padEnd(7))}`);
      }
    } else {
      console.log(`  ${roleCol} ${providerCol} ${modelCol}`);
    }
  }

  console.log();
}

/**
 * Configure role-based routing interactively
 */
async function configureRoleBasedRouting(
  providers: ProviderConfig[],
  optimizationPreference?: OptimizationPreference
): Promise<RoutingRuleConfig[]> {
  const rules: RoutingRuleConfig[] = [];
  const providerNames = providers.map(p => p.name);

  // Ask if user wants role-based routing
  const wantRoleRouting = await p.select({
    message: "How would you like to configure role-based routing?",
    options: [
      {
        value: "quick",
        label: "Quick setup",
        hint: optimizationPreference
          ? `Auto-configure using ${optimizationPreference} optimization`
          : "Use recommended providers for all roles",
      },
      {
        value: "custom",
        label: "Custom",
        hint: "Choose provider for each role",
      },
      {
        value: "skip",
        label: "Skip role-based routing",
        hint: "Only use pre-built rules",
      },
    ],
  });

  if (p.isCancel(wantRoleRouting) || wantRoleRouting === "skip") {
    return rules;
  }

  // Filter roles to only those with available providers
  const availableRoles = AGENT_ROLES.filter(role => {
    // Check if recommended provider is available, or if any provider is available
    return providerNames.includes(role.recommendedProvider) || providerNames.length > 0;
  });

  if (wantRoleRouting === "quick") {
    // Quick setup - use intelligent assignments based on optimization preference
    const roleConfigs: Array<{ role: AgentRole; provider: string; model: string }> = [];

    if (optimizationPreference) {
      // Use intelligent assignments based on preference
      const assignments = generateIntelligentAssignments(providerNames, optimizationPreference);

      p.log.info(color.dim(getPreferenceDescription(optimizationPreference)));

      for (const role of availableRoles) {
        const assignment = assignments.get(role.value);
        if (assignment) {
          rules.push(roleToRule(role, assignment.provider, assignment.model));
          roleConfigs.push({ role, provider: assignment.provider, model: assignment.model });
        } else {
          // Fallback: no suitable model found in benchmarks, use static defaults
          let targetProvider: string;
          let targetModel: string;

          if (providerNames.includes(role.recommendedProvider)) {
            targetProvider = role.recommendedProvider;
            targetModel = role.recommendedModel;
          } else {
            targetProvider = providerNames[0]!;
            const provider = providers.find(p => p.name === targetProvider);
            targetModel = provider?.default_model ?? role.recommendedModel;
          }

          rules.push(roleToRule(role, targetProvider, targetModel));
          roleConfigs.push({ role, provider: targetProvider, model: targetModel });
        }
      }
    } else {
      // Legacy mode: use static recommended providers
      for (const role of availableRoles) {
        let targetProvider: string;
        let targetModel: string;

        // Use recommended provider if available, otherwise use first available
        if (providerNames.includes(role.recommendedProvider)) {
          targetProvider = role.recommendedProvider;
          targetModel = role.recommendedModel;
        } else {
          // Fallback to first available provider
          targetProvider = providerNames[0]!;
          const provider = providers.find(p => p.name === targetProvider);
          targetModel = provider?.default_model ?? role.recommendedModel;
        }

        rules.push(roleToRule(role, targetProvider, targetModel));
        roleConfigs.push({ role, provider: targetProvider, model: targetModel });
      }
    }

    displayRoleSummary(roleConfigs, optimizationPreference);
    p.log.success(`Created ${rules.length} role-based routing rules`);
    return rules;
  }

  // Custom setup - let user select roles and providers
  p.log.message(color.dim("Use ↑↓ to navigate, space to select, enter to confirm."));

  const selectedRoles = await p.multiselect({
    message: "Which roles would you like to configure?",
    options: availableRoles.map(role => ({
      value: role.value,
      label: role.label,
      hint: role.hint,
    })),
    required: false,
  });

  if (p.isCancel(selectedRoles) || (selectedRoles as string[]).length === 0) {
    return rules;
  }

  const roleConfigs: Array<{ role: AgentRole; provider: string; model: string }> = [];

  // Configure each selected role
  for (const roleValue of selectedRoles as string[]) {
    const role = availableRoles.find(r => r.value === roleValue);
    if (!role) continue;

    console.log();
    p.log.step(color.cyan(`Configuring: ${role.label}`));
    console.log(color.dim(`  Detection patterns: ${role.patterns.slice(0, 5).join(", ")}...`));

    // Build provider options
    const providerOptions: Array<{ value: string; label: string; hint?: string }> = providers.map(provider => {
      const isRecommended = provider.name === role.recommendedProvider;
      const option: { value: string; label: string; hint?: string } = {
        value: provider.name,
        label: provider.name,
      };
      if (isRecommended) {
        option.hint = `${role.recommendedModel} [Recommended]`;
      }
      return option;
    });

    // Add skip option
    providerOptions.push({
      value: "__skip__",
      label: "Skip this role",
      hint: "Don't create a rule for this role",
    });

    const selectedProvider = await p.select({
      message: `Which provider should handle ${role.label.toLowerCase()} tasks?`,
      options: providerOptions,
    });

    if (p.isCancel(selectedProvider) || selectedProvider === "__skip__") {
      continue;
    }

    // Get the selected provider config
    const providerConfig = providers.find(p => p.name === selectedProvider);
    if (!providerConfig) continue;

    // Let user select model
    const modelOptions = getProviderModels(providerConfig);

    // Add recommended model to top if not present
    const recommendedInList = modelOptions.some(m => m.value === role.recommendedModel);
    if (!recommendedInList && providerConfig.name === role.recommendedProvider) {
      modelOptions.unshift({
        value: role.recommendedModel,
        label: role.recommendedModel,
        hint: "Recommended",
      });
    }

    const selectedModel = await p.select({
      message: "Select model:",
      options: modelOptions,
    });

    if (p.isCancel(selectedModel)) continue;

    const rule = roleToRule(role, selectedProvider as string, selectedModel as string);
    rules.push(rule);
    roleConfigs.push({ role, provider: selectedProvider as string, model: selectedModel as string });

    // Show confirmation
    console.log(color.green(`  ✓ Rule created: ${rule.name}`));
    console.log(color.dim(`    Patterns: ${role.patterns.slice(0, 4).join(", ")}...`));
    console.log(color.dim(`    Provider: ${selectedProvider} → ${selectedModel}`));
    console.log(color.dim(`    Priority: ${rule.priority}`));
  }

  // Display summary if any rules were created
  if (roleConfigs.length > 0) {
    displayRoleSummary(roleConfigs);
    p.log.success(`Created ${rules.length} role-based routing rules`);
  }

  return rules;
}

/**
 * Configure routing rules interactively
 */
async function configureRouting(
  providers: ProviderConfig[],
  optimizationPreference?: OptimizationPreference
): Promise<RoutingRuleConfig[]> {
  p.log.step(color.bold("Routing Rules"));

  const rules: RoutingRuleConfig[] = [];
  const providerNames = providers.map((p) => p.name);

  // Check for Ollama for privacy routing
  const hasOllama = providers.some((p) => p.type === "ollama");
  const hasZai = providers.some((p) => p.type === "zai");
  const hasAnthropic = providers.some((p) => p.type === "anthropic");

  if (hasOllama) {
    const privacyRoute = await p.confirm({
      message: "Route sensitive content to local Ollama? (passwords, keys, secrets)",
      initialValue: true,
    });

    if (!p.isCancel(privacyRoute) && privacyRoute) {
      rules.push({
        name: "privacy-routing",
        conditions: {
          keywords: [
            "password",
            "secret",
            "api_key",
            "apikey",
            "token",
            "credential",
            "private",
            ".env",
            "ssh",
            "certificate",
          ],
        },
        route_to: { provider: "ollama" },
        priority: 100,
        enabled: true,
      });
      p.log.success("Added privacy routing rule");
    }
  }

  if (hasZai) {
    const largeRoute = await p.confirm({
      message: "Route large requests (>50k tokens) to Z.AI for cost savings?",
      initialValue: true,
    });

    if (!p.isCancel(largeRoute) && largeRoute) {
      rules.push({
        name: "large-content-routing",
        conditions: {
          min_tokens: 50000,
        },
        route_to: { provider: "zai" },
        priority: 50,
        enabled: true,
      });
      p.log.success("Added large content routing rule");
    }
  }

  if (hasAnthropic && providers.length > 1) {
    const complexRoute = await p.confirm({
      message: "Route complex reasoning tasks to Claude? (debugging, architecture)",
      initialValue: true,
    });

    if (!p.isCancel(complexRoute) && complexRoute) {
      rules.push({
        name: "complex-reasoning",
        conditions: {
          keywords: [
            "debug",
            "architecture",
            "refactor",
            "optimize",
            "design pattern",
            "security audit",
            "code review",
          ],
        },
        route_to: { provider: "anthropic" },
        priority: 75,
        enabled: true,
      });
      p.log.success("Added complex reasoning routing rule");
    }
  }

  // Add role-based routing section
  console.log();
  p.log.step(color.bold("Role-Based Routing"));
  p.log.info(color.dim("Route requests to different LLMs based on the agent task type"));

  const roleRules = await configureRoleBasedRouting(providers, optimizationPreference);
  rules.push(...roleRules);

  // Option to add custom rules
  p.log.message(color.dim("Custom rules let you route by specific keywords, patterns, or token counts."));
  p.log.message(color.dim("Most users don't need this - the defaults above are comprehensive."));

  const addCustom = await p.confirm({
    message: "Add custom routing rules? (advanced)",
    initialValue: false,
  });

  if (!p.isCancel(addCustom) && addCustom) {
    let addMore = true;

    while (addMore) {
      const ruleName = await p.text({
        message: "Rule name:",
        placeholder: "my-custom-rule",
        validate: (v) => (v.length > 0 ? undefined : "Name required"),
      });

      if (p.isCancel(ruleName)) break;

      const keywords = await p.text({
        message: "Keywords to match (comma-separated):",
        placeholder: "keyword1, keyword2",
      });

      if (p.isCancel(keywords)) break;

      const targetProvider = await p.select({
        message: "Route to provider:",
        options: providerNames.map((n) => ({ value: n, label: n })),
      });

      if (p.isCancel(targetProvider)) break;

      rules.push({
        name: ruleName as string,
        conditions: {
          keywords: (keywords as string)
            .split(",")
            .map((k) => k.trim())
            .filter((k) => k.length > 0),
        },
        route_to: { provider: targetProvider as string },
        priority: 25,
        enabled: true,
      });

      p.log.success(`Added rule: ${ruleName}`);

      const continueAdding = await p.confirm({
        message: "Add another rule?",
        initialValue: false,
      });

      addMore = !p.isCancel(continueAdding) && continueAdding;
    }
  }

  return rules;
}

/**
 * Generate YAML configuration
 */
function generateConfig(
  providers: ProviderConfig[],
  rules: RoutingRuleConfig[],
  defaultProvider: string
): GatewayConfig {
  return {
    server: {
      listen: "127.0.0.1:8787",
      timeout_ms: 300000,
    },
    routing: {
      default_provider: defaultProvider,
      rules,
    },
    providers,
    health: {
      check_interval_seconds: 30,
      circuit_breaker: {
        failure_threshold: 3,
        recovery_timeout_seconds: 60,
      },
    },
    logging: {
      level: "info",
      format: "text",
    },
  };
}

/**
 * Syntax highlight YAML for display
 */
function highlightYaml(yamlStr: string): string {
  return yamlStr
    .split("\n")
    .map((line) => {
      // Highlight keys
      if (line.includes(":")) {
        const [key, ...rest] = line.split(":");
        const value = rest.join(":");

        // Check if it's a key-value pair
        if (key && key.trim() && !key.trim().startsWith("-")) {
          return color.cyan(key) + ":" + highlightValue(value);
        }
      }

      // Highlight list items
      if (line.trim().startsWith("-")) {
        const indent = line.match(/^(\s*)/)?.[1] ?? "";
        const content = line.slice(indent.length + 1).trim();

        if (content.includes(":")) {
          const [key, ...rest] = content.split(":");
          return indent + color.dim("-") + " " + color.cyan(key) + ":" + highlightValue(rest.join(":"));
        }

        return indent + color.dim("-") + " " + content;
      }

      return line;
    })
    .join("\n");
}

/**
 * Highlight YAML value
 */
function highlightValue(value: string): string {
  const trimmed = value.trim();

  // Boolean
  if (trimmed === "true" || trimmed === "false") {
    return " " + color.yellow(trimmed);
  }

  // Number
  if (/^\d+$/.test(trimmed)) {
    return " " + color.magenta(trimmed);
  }

  // String with quotes (likely sensitive)
  if (trimmed.startsWith('"') || trimmed.startsWith("'")) {
    return " " + color.green(trimmed);
  }

  // URL
  if (trimmed.startsWith("http")) {
    return " " + color.blue(trimmed);
  }

  return value;
}

/**
 * Get provider type description for summary display
 */
function getProviderTypeLabel(provider: ProviderConfig): string {
  if (provider.subscription_passthrough) {
    return "subscription";
  }
  if (provider.type === "ollama") {
    return "local";
  }
  return "API key";
}

/**
 * Generate a compact configuration summary instead of full YAML dump
 */
function generateConfigSummary(
  config: GatewayConfig,
  configPath: string
): string {
  const lines: string[] = [];

  // Server info
  lines.push(`  ${color.bold("Server:")} ${config.server.listen}`);
  lines.push(`  ${color.bold("Default Provider:")} ${config.routing.default_provider}`);
  lines.push("");

  // Providers summary
  const enabledProviders = config.providers.filter((p) => p.enabled !== false);
  lines.push(`  ${color.bold(`Providers (${enabledProviders.length}):`)}` );
  for (const provider of enabledProviders) {
    const typeLabel = getProviderTypeLabel(provider);
    lines.push(`    ${color.green("✓")} ${provider.name} ${color.dim(`(${typeLabel})`)}`);
  }
  lines.push("");

  // Routing rules summary
  const rules = config.routing.rules ?? [];
  if (rules.length > 0) {
    lines.push(`  ${color.bold(`Routing Rules (${rules.length}):`)}`);

    // Sort by priority descending and take top 4
    const sortedRules = [...rules].sort((a, b) => (b.priority ?? 0) - (a.priority ?? 0));
    const displayRules = sortedRules.slice(0, 4);

    for (const rule of displayRules) {
      const ruleName = rule.name.replace(/-routing$/, "").replace(/-/g, " ");
      const capitalizedName = ruleName.charAt(0).toUpperCase() + ruleName.slice(1);
      const provider = rule.route_to.provider;
      const priority = rule.priority ?? 0;
      lines.push(`    ${color.dim("•")} ${capitalizedName} ${color.dim("→")} ${provider} ${color.dim(`(priority ${priority})`)}`);
    }

    if (rules.length > 4) {
      lines.push(`    ${color.dim(`... and ${rules.length - 4} more`)}`);
    }
    lines.push("");
  }

  // Config path
  lines.push(`  ${color.bold("Config:")} ${color.dim(configPath)}`);

  return lines.join("\n");
}

/**
 * Display environment variables summary with export commands
 */
function displayEnvVarsSummary(): string {
  if (configuredEnvVars.size === 0) {
    return "";
  }

  const lines: string[] = [];
  lines.push(color.bold("Set these environment variables before running the gateway:"));
  lines.push("");

  for (const [envVar] of configuredEnvVars) {
    lines.push(`  ${color.cyan(`export ${envVar}="your-api-key-here"`)}`);
  }

  lines.push("");
  lines.push(color.dim("Add these to your ~/.zshrc or ~/.bashrc for persistence."));

  return lines.join("\n");
}

/**
 * Get the appropriate shell profile path based on the current shell
 */
function getShellProfilePath(): string {
  const shell = process.env.SHELL ?? "";
  if (shell.includes("zsh")) {
    return path.join(os.homedir(), ".zshrc");
  }
  return path.join(os.homedir(), ".bashrc");
}

/**
 * Add environment variables to the user's shell profile
 */
async function addEnvVarsToShellProfile(): Promise<void> {
  const profilePath = getShellProfilePath();
  const profileName = path.basename(profilePath);

  try {
    // Read existing profile content (or empty string if file doesn't exist)
    let profileContent = "";
    if (fs.existsSync(profilePath)) {
      profileContent = fs.readFileSync(profilePath, "utf-8");
    }

    // Collect env vars to add
    const envVarsToAdd: Array<{ name: string; value: string }> = [];

    // Add configured provider API keys
    for (const [envVar, value] of configuredEnvVars) {
      // Check if this env var already exists in the profile
      const existsPattern = new RegExp(`^\\s*export\\s+${envVar}=`, "m");
      if (!existsPattern.test(profileContent)) {
        envVarsToAdd.push({ name: envVar, value });
      }
    }

    // Add ANTHROPIC_BASE_URL if not already present
    const baseUrlVar = "ANTHROPIC_BASE_URL";
    const baseUrlValue = "http://localhost:8787";
    const baseUrlPattern = new RegExp(`^\\s*export\\s+${baseUrlVar}=`, "m");
    if (!baseUrlPattern.test(profileContent)) {
      envVarsToAdd.push({ name: baseUrlVar, value: baseUrlValue });
    }

    if (envVarsToAdd.length === 0) {
      p.log.info(`All environment variables already exist in ~/${profileName}`);
      return;
    }

    // Build the export block
    const exportLines: string[] = [];
    exportLines.push("# Ludicrous Gateway");
    for (const { name, value } of envVarsToAdd) {
      exportLines.push(`export ${name}="${value}"`);
    }
    const exportBlock = exportLines.join("\n");

    // Ensure we add a newline before our block if the file doesn't end with one
    let prefix = "";
    if (profileContent.length > 0 && !profileContent.endsWith("\n")) {
      prefix = "\n";
    }
    // Add an extra blank line for separation
    if (profileContent.length > 0) {
      prefix += "\n";
    }

    // Append to profile
    fs.appendFileSync(profilePath, prefix + exportBlock + "\n", "utf-8");

    p.log.success(`Added environment variables to ~/${profileName}`);
    p.log.info(`Run ${color.cyan(`source ~/${profileName}`)} to apply changes (or restart your terminal)`);
  } catch (error) {
    p.log.error(
      `Failed to update ${profilePath}: ${error instanceof Error ? error.message : "Unknown error"}`
    );
    p.log.info("You'll need to add the environment variables manually.");
  }
}

/**
 * Clear configured env vars (for fresh setup)
 */
function clearConfiguredEnvVars(): void {
  configuredEnvVars.clear();
}

/**
 * Get the data directory for ludicrous-gateway
 */
function getDataDir(): string {
  return path.join(os.homedir(), ".local", "share", "ludicrous-gateway");
}

/**
 * Find the absolute path to the node executable
 * This is needed for LaunchAgents which run with minimal PATH
 */
function findNodePath(): string | null {
  try {
    const result = execFileSync("which", ["node"], { encoding: "utf-8" });
    return result.trim();
  } catch {
    // Try common locations if 'which' fails
    const commonPaths = [
      "/opt/homebrew/bin/node",     // Apple Silicon Homebrew
      "/usr/local/bin/node",        // Intel Homebrew / manual install
      "/usr/bin/node",              // System node
    ];

    for (const nodePath of commonPaths) {
      if (fs.existsSync(nodePath)) {
        return nodePath;
      }
    }

    // Try to find NVM node (check common NVM installation paths)
    const nvmDir = process.env.NVM_DIR ?? path.join(os.homedir(), ".nvm");
    const nvmVersionsDir = path.join(nvmDir, "versions", "node");
    if (fs.existsSync(nvmVersionsDir)) {
      try {
        const versions = fs.readdirSync(nvmVersionsDir);
        if (versions.length > 0) {
          // Sort versions and use the latest one
          const sortedVersions = versions.sort((a, b) => {
            const aVer = a.replace("v", "").split(".").map(Number);
            const bVer = b.replace("v", "").split(".").map(Number);
            for (let i = 0; i < 3; i++) {
              if ((aVer[i] ?? 0) !== (bVer[i] ?? 0)) {
                return (bVer[i] ?? 0) - (aVer[i] ?? 0);
              }
            }
            return 0;
          });
          const latestVersion = sortedVersions[0];
          if (latestVersion) {
            const nvmNodePath = path.join(nvmVersionsDir, latestVersion, "bin", "node");
            if (fs.existsSync(nvmNodePath)) {
              return nvmNodePath;
            }
          }
        }
      } catch {
        // Ignore errors reading NVM directory
      }
    }

    return null;
  }
}

/**
 * Find the ludicrous-gateway binary path
 */
function findBinaryPath(): string | null {
  try {
    // Use 'which' on Unix-like systems
    const result = execFileSync("which", ["ludicrous-gateway"], { encoding: "utf-8" });
    return result.trim();
  } catch {
    return null;
  }
}

/**
 * Install background service to start gateway on login
 */
async function installBackgroundService(): Promise<void> {
  const platform = process.platform;

  // Find the ludicrous-gateway binary path
  const binaryPath = findBinaryPath();
  if (!binaryPath) {
    p.log.warn("Could not find ludicrous-gateway binary in PATH. Skipping background service installation.");
    p.log.info("You can manually start the gateway with: ludicrous-gateway serve");
    return;
  }

  // Create log directory
  const logDir = path.join(getDataDir(), "logs");
  try {
    if (!fs.existsSync(logDir)) {
      fs.mkdirSync(logDir, { recursive: true });
    }
  } catch (error) {
    p.log.warn(`Could not create log directory: ${error instanceof Error ? error.message : "Unknown error"}`);
  }

  if (platform === "darwin") {
    // Find node path for macOS LaunchAgent (needed because LaunchAgents run with minimal PATH)
    const nodePath = findNodePath();
    if (!nodePath) {
      p.log.warn("Could not find Node.js installation. The gateway may not start automatically on login.");
      p.log.info("Ensure Node.js is installed in a standard location (/opt/homebrew/bin, /usr/local/bin, or via NVM)");
    }
    await installMacOSLaunchAgent(binaryPath, logDir, nodePath);
  } else if (platform === "linux") {
    await installLinuxSystemdService(binaryPath);
  } else {
    p.log.info(`Background service installation not supported on ${platform}. Start manually with: ludicrous-gateway serve`);
  }
}

/**
 * Install macOS LaunchAgent
 */
async function installMacOSLaunchAgent(binaryPath: string, logDir: string, nodePath: string | null): Promise<void> {
  const launchAgentDir = path.join(os.homedir(), "Library", "LaunchAgents");
  const plistPath = path.join(launchAgentDir, "com.ludicrous-gateway.plist");

  // Build environment variables dict
  const envVarsXml: string[] = [];

  // Always add PATH to ensure child processes have access to common binaries
  // This is a fallback for any child processes the gateway might spawn
  const defaultPath = "/opt/homebrew/bin:/usr/local/bin:/usr/bin:/bin:/usr/sbin:/sbin";
  envVarsXml.push(`      <key>PATH</key>`);
  envVarsXml.push(`      <string>${defaultPath}</string>`);

  for (const [envVar, value] of configuredEnvVars) {
    // Escape XML special characters
    const escapedValue = value
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;");
    envVarsXml.push(`      <key>${envVar}</key>`);
    envVarsXml.push(`      <string>${escapedValue}</string>`);
  }

  // Build ProgramArguments based on whether we have node path
  // When nodePath is available, we run: node /path/to/ludicrous-gateway serve
  // This is necessary because the shebang (#!/usr/bin/env node) won't work
  // when LaunchAgents run with minimal PATH at boot time
  let programArgumentsXml: string;
  if (nodePath) {
    programArgumentsXml = `    <key>ProgramArguments</key>
    <array>
        <string>${nodePath}</string>
        <string>${binaryPath}</string>
        <string>serve</string>
    </array>`;
  } else {
    // Fallback: try to run the script directly (may fail if PATH doesn't include node)
    programArgumentsXml = `    <key>ProgramArguments</key>
    <array>
        <string>${binaryPath}</string>
        <string>serve</string>
    </array>`;
  }

  const plistContent = `<?xml version="1.0" encoding="UTF-8"?>
<!DOCTYPE plist PUBLIC "-//Apple//DTD PLIST 1.0//EN" "http://www.apple.com/DTDs/PropertyList-1.0.dtd">
<plist version="1.0">
<dict>
    <key>Label</key>
    <string>com.ludicrous-gateway</string>
${programArgumentsXml}
    <key>RunAtLoad</key>
    <true/>
    <key>KeepAlive</key>
    <true/>
    <key>StandardOutPath</key>
    <string>${path.join(logDir, "stdout.log")}</string>
    <key>StandardErrorPath</key>
    <string>${path.join(logDir, "stderr.log")}</string>
    <key>EnvironmentVariables</key>
    <dict>
${envVarsXml.join("\n")}
    </dict>
</dict>
</plist>
`;

  try {
    // Ensure LaunchAgents directory exists
    if (!fs.existsSync(launchAgentDir)) {
      fs.mkdirSync(launchAgentDir, { recursive: true });
    }

    // Unload existing service if present
    try {
      execFileSync("launchctl", ["unload", plistPath], { encoding: "utf-8", stdio: "pipe" });
    } catch {
      // Ignore errors if service wasn't loaded
    }

    // Write plist file
    fs.writeFileSync(plistPath, plistContent, "utf-8");

    // Load the service
    execFileSync("launchctl", ["load", plistPath], { encoding: "utf-8" });

    p.log.success("Installed background service (LaunchAgent)");
    p.log.info(`The gateway will now start automatically on login`);
    p.log.info(`Logs: ${color.dim(logDir)}`);
  } catch (error) {
    p.log.warn(`Could not install background service: ${error instanceof Error ? error.message : "Unknown error"}`);
    p.log.info("You can manually start the gateway with: ludicrous-gateway serve");
  }
}

/**
 * Install Linux systemd user service
 */
async function installLinuxSystemdService(binaryPath: string): Promise<void> {
  const systemdUserDir = path.join(os.homedir(), ".config", "systemd", "user");
  const servicePath = path.join(systemdUserDir, "ludicrous-gateway.service");

  // Build environment lines
  const envLines: string[] = [];
  for (const [envVar, value] of configuredEnvVars) {
    // Escape special characters for systemd
    const escapedValue = value.replace(/"/g, '\\"');
    envLines.push(`Environment="${envVar}=${escapedValue}"`);
  }

  const serviceContent = `[Unit]
Description=Ludicrous Gateway - LLM API Router
After=network.target

[Service]
Type=simple
ExecStart=${binaryPath} serve
Restart=always
RestartSec=5
${envLines.join("\n")}

[Install]
WantedBy=default.target
`;

  try {
    // Ensure systemd user directory exists
    if (!fs.existsSync(systemdUserDir)) {
      fs.mkdirSync(systemdUserDir, { recursive: true });
    }

    // Write service file
    fs.writeFileSync(servicePath, serviceContent, "utf-8");

    // Reload systemd and enable/start the service
    execFileSync("systemctl", ["--user", "daemon-reload"], { encoding: "utf-8" });
    execFileSync("systemctl", ["--user", "enable", "--now", "ludicrous-gateway"], { encoding: "utf-8" });

    p.log.success("Installed background service (systemd user service)");
    p.log.info(`The gateway will now start automatically on login`);
    p.log.info(`Check status: ${color.cyan("systemctl --user status ludicrous-gateway")}`);
    p.log.info(`View logs: ${color.cyan("journalctl --user -u ludicrous-gateway -f")}`);
  } catch (error) {
    p.log.warn(`Could not install background service: ${error instanceof Error ? error.message : "Unknown error"}`);
    p.log.info("You can manually start the gateway with: ludicrous-gateway serve");
  }
}

/**
 * Get config file path
 */
function getConfigPath(): string {
  const configDir = path.join(os.homedir(), ".config", "ludicrous-gateway");
  return path.join(configDir, "config.yaml");
}

/**
 * Save configuration to file
 */
async function saveConfig(config: GatewayConfig): Promise<boolean> {
  const configPath = getConfigPath();
  const configDir = path.dirname(configPath);

  try {
    // Create directory if needed
    if (!fs.existsSync(configDir)) {
      fs.mkdirSync(configDir, { recursive: true });
    }

    // Backup existing config
    if (fs.existsSync(configPath)) {
      const backupPath = `${configPath}.backup.${Date.now()}`;
      fs.copyFileSync(configPath, backupPath);
      p.log.info(`Backed up existing config to: ${color.dim(backupPath)}`);
    }

    // Write new config
    const yamlContent = yaml.dump(config, {
      indent: 2,
      lineWidth: 120,
      noRefs: true,
    });

    fs.writeFileSync(configPath, yamlContent, "utf-8");
    return true;
  } catch (error) {
    p.log.error(
      `Failed to save config: ${error instanceof Error ? error.message : "Unknown error"}`
    );
    return false;
  }
}

/**
 * Run the main setup wizard
 */
export async function runSetupWizard(): Promise<void> {
  displayBanner();

  p.intro(color.bgCyan(color.black(" Welcome to Ludicrous Gateway ")));

  // Provider selection
  p.log.message(color.dim("Use ↑↓ to navigate, space to select, enter to confirm. Select multiple providers."));

  const selectedProviders = await p.multiselect({
    message: "Which providers would you like to configure?",
    options: AVAILABLE_PROVIDERS.map((provider) => ({
      value: provider.value,
      label: provider.label,
      ...(provider.hint ? { hint: provider.hint } : {}),
    })),
    required: true,
  });

  if (p.isCancel(selectedProviders)) {
    p.cancel("Setup cancelled");
    process.exit(0);
  }

  if ((selectedProviders as string[]).includes("kimi")) {
    p.log.warn("Kimi K2 support is coming soon - skipping for now");
  }

  const providers: ProviderConfig[] = [];

  // Configure each selected provider
  for (const providerType of selectedProviders as string[]) {
    if (providerType === "kimi") continue;

    let config: ProviderConfig | null = null;

    switch (providerType) {
      case "anthropic":
        config = await configureAnthropic();
        break;
      case "zai":
        config = await configureZai();
        break;
      case "ollama":
        config = await configureOllama();
        break;
      case "openai":
        config = await configureOpenAI();
        break;
      case "gemini":
        config = await configureGemini();
        break;
      case "grok":
        config = await configureGrok();
        break;
      case "mistral":
        config = await configureMistral();
        break;
      case "deepseek":
        config = await configureDeepSeek();
        break;
    }

    if (config) {
      providers.push(config);
      p.log.success(`Configured ${color.cyan(config.name)} provider`);
    }
  }

  if (providers.length === 0) {
    p.cancel("No providers configured. Please run setup again.");
    process.exit(1);
  }

  // Ask for optimization preference
  console.log();
  p.log.step(color.bold("Optimization Strategy"));

  const optimizationPreference = await p.select({
    message: "What's your optimization priority?",
    options: [
      { value: "quality", label: "Best Quality", hint: "Use top-tier models, higher cost" },
      { value: "balanced", label: "Balanced", hint: "Quality for critical tasks, cost-effective for bulk (Recommended)" },
      { value: "cheap", label: "Cost Optimized", hint: "Minimize costs, acceptable quality" },
    ],
  });

  const preference: OptimizationPreference | undefined = p.isCancel(optimizationPreference)
    ? undefined
    : (optimizationPreference as OptimizationPreference);

  // Select default provider
  let defaultProvider = providers[0]!.name;

  if (providers.length > 1) {
    const selected = await p.select({
      message: "Select default provider (when no rules match):",
      options: providers.map((p) => ({
        value: p.name,
        label: `${p.name} (${p.type})`,
      })),
    });

    if (!p.isCancel(selected)) {
      defaultProvider = selected as string;
    }
  }

  // Configure routing rules
  const rules = await configureRouting(providers, preference);

  // Generate config
  const config = generateConfig(providers, rules, defaultProvider);
  const configPath = getConfigPath();

  // Preview config with compact summary
  console.log();
  p.note(generateConfigSummary(config, configPath), "Configuration Summary");

  // Confirm save
  const saveConfirm = await p.confirm({
    message: `Save configuration to ${color.cyan(configPath)}?`,
    initialValue: true,
  });

  if (p.isCancel(saveConfirm) || !saveConfirm) {
    // Offer to print full config instead
    const printConfig = await p.confirm({
      message: "Print full YAML configuration to console instead?",
      initialValue: true,
    });

    if (!p.isCancel(printConfig) && printConfig) {
      const yamlOutput = yaml.dump(config, { indent: 2, lineWidth: 120 });
      console.log("\n" + highlightYaml(yamlOutput));
    }

    p.outro(color.yellow("Configuration not saved"));
    return;
  }

  // Save config
  const saved = await saveConfig(config);

  if (!saved) {
    p.outro(color.red("Failed to save configuration"));
    return;
  }

  // Success outro
  console.log();

  // Show environment variables summary if any were configured
  const envVarsSummary = displayEnvVarsSummary();
  if (envVarsSummary) {
    p.note(envVarsSummary, "Required Environment Variables");
    console.log();

    // Auto-add environment variables to shell profile
    await addEnvVarsToShellProfile();
    console.log();
  }

  // Install background service to start gateway on login
  await installBackgroundService();
  console.log();

  const shellProfile = getShellProfilePath();
  p.note(
    `${color.bold("1. Restart your terminal or run:")}\n` +
      `  ${color.cyan(`source ${shellProfile}`)}\n\n` +
      `${color.bold("2. Run Claude Code - the gateway is already running!")}`,
    "Next Steps"
  );

  // Clear env vars tracking for next run
  clearConfiguredEnvVars();

  p.outro(color.green("Setup complete!"));
}

/**
 * Add a single provider interactively
 */
export async function addProvider(): Promise<void> {
  p.intro(color.bgCyan(color.black(" Add Provider ")));

  const providerType = await p.select({
    message: "Select provider to add:",
    options: AVAILABLE_PROVIDERS.filter((provider) => provider.value !== "kimi").map((provider) => ({
      value: provider.value,
      label: provider.label,
      ...(provider.hint ? { hint: provider.hint } : {}),
    })),
  });

  if (p.isCancel(providerType)) {
    p.cancel("Cancelled");
    return;
  }

  let config: ProviderConfig | null = null;

  switch (providerType) {
    case "anthropic":
      config = await configureAnthropic();
      break;
    case "zai":
      config = await configureZai();
      break;
    case "ollama":
      config = await configureOllama();
      break;
    case "openai":
      config = await configureOpenAI();
      break;
    case "gemini":
      config = await configureGemini();
      break;
    case "grok":
      config = await configureGrok();
      break;
    case "mistral":
      config = await configureMistral();
      break;
    case "deepseek":
      config = await configureDeepSeek();
      break;
  }

  if (!config) {
    p.cancel("Provider configuration cancelled");
    return;
  }

  // Load existing config and add provider
  const configPath = getConfigPath();
  let existingConfig: GatewayConfig;

  try {
    if (fs.existsSync(configPath)) {
      const content = fs.readFileSync(configPath, "utf-8");
      existingConfig = yaml.load(content) as GatewayConfig;
    } else {
      // Create new config with just this provider
      existingConfig = generateConfig([config], [], config.name);
      existingConfig.providers = [];
    }
  } catch {
    p.log.error("Failed to load existing config, creating new one");
    existingConfig = generateConfig([config], [], config.name);
    existingConfig.providers = [];
  }

  // Check if provider already exists
  const existingIndex = existingConfig.providers.findIndex(
    (p) => p.name === config!.name
  );

  if (existingIndex >= 0) {
    const replace = await p.confirm({
      message: `Provider "${config.name}" already exists. Replace it?`,
      initialValue: true,
    });

    if (p.isCancel(replace) || !replace) {
      p.cancel("Cancelled");
      return;
    }

    existingConfig.providers[existingIndex] = config;
  } else {
    existingConfig.providers.push(config);
  }

  // Save updated config
  const saved = await saveConfig(existingConfig);

  if (saved) {
    // Show environment variables summary if any were configured
    const envVarsSummary = displayEnvVarsSummary();
    if (envVarsSummary) {
      console.log();
      p.note(envVarsSummary, "Required Environment Variables");
    }

    // Clear env vars tracking for next run
    clearConfiguredEnvVars();

    p.outro(color.green(`Added ${config.name} provider`));
  } else {
    p.outro(color.red("Failed to save configuration"));
  }
}

/**
 * List configured providers
 */
export async function listProviders(): Promise<void> {
  p.intro(color.bgCyan(color.black(" Configured Providers ")));

  const configPath = getConfigPath();

  if (!fs.existsSync(configPath)) {
    p.log.warn("No configuration found. Run: ludicrous-gateway setup");
    p.outro(color.yellow("No providers configured"));
    return;
  }

  try {
    const content = fs.readFileSync(configPath, "utf-8");
    const config = yaml.load(content) as GatewayConfig;

    if (!config.providers || config.providers.length === 0) {
      p.log.warn("No providers configured");
      p.outro(color.yellow("Run: ludicrous-gateway setup"));
      return;
    }

    console.log();

    for (const provider of config.providers) {
      const status = provider.enabled ? color.green("enabled") : color.red("disabled");
      const isDefault = provider.name === config.routing.default_provider;

      console.log(
        `  ${color.bold(provider.name)} ${color.dim(`(${provider.type})`)} - ${status}` +
          (isDefault ? color.yellow(" [default]") : "")
      );

      if (provider.base_url) {
        console.log(`    ${color.dim("URL:")} ${provider.base_url}`);
      }

      if (provider.default_model) {
        console.log(`    ${color.dim("Model:")} ${provider.default_model}`);
      }

      console.log();
    }

    p.log.info(`Config file: ${color.dim(configPath)}`);
    p.outro(`${config.providers.length} provider(s) configured`);
  } catch (error) {
    p.log.error(
      `Failed to read config: ${error instanceof Error ? error.message : "Unknown error"}`
    );
    p.outro(color.red("Error reading configuration"));
  }
}
